import { ReactElement } from "react";
import {
  StepType,
  IUploaderColumnConfig,
  RawData,
  Data,
  ValidationResult,
  StepChangeEvent,
  UploaderSubmissionData
} from "../../types/types";
import { WorkBook } from "xlsx-ugnis";
import { GridTypes } from "../../../../../enums";

export interface IBaseAddon {
  name: string;
  applicableSteps: StepType[];
  render: (step: StepType, data: any) => ReactElement;
  afterUpload?: (workbook: WorkBook, file: File) => Promise<void>;
  validate?: (data: any, step: StepType) => ValidationResult;
}

export interface AddonParams<T extends string = string> {
  gridType: GridTypes;
  fields: IUploaderColumnConfig[];
  maxRecords?: number;
  maxFileSize?: number;
  autoMapHeaders?: boolean;
  autoMapEntityRelationValues?: boolean;
  dateFormat?: string;
  parseRaw?: boolean;

  organisationId?: number;
  projectId?: number;

  uploadStepHook?: (data: RawData[]) => Promise<RawData[]>;
  selectHeaderStepHook?: (
    headerValues: RawData,
    data: RawData[]
  ) => Promise<{ headerValues: RawData; data: RawData[] }>;
  matchColumnsStepHook?: (table: Data<T>[], rawData: RawData[], columns: any) => Promise<Data<T>[]>;
  onSubmit?: (data: UploaderSubmissionData<any>) => void | Promise<any>;

  registeredAddons: BaseAddon[];
  conflictFields?: string[];
}

export abstract class BaseAddon<T extends string = string> implements IBaseAddon {
  name: string;
  applicableSteps: StepType[];
  uploadedFile: File | null = null;
  onNext: (event: StepChangeEvent) => void;

  gridType: GridTypes;
  fields: IUploaderColumnConfig[];
  maxRecords?: number;
  maxFileSize?: number;
  autoMapHeaders?: boolean;
  autoMapEntityRelationValues?: boolean;
  dateFormat?: string;
  parseRaw?: boolean;

  uploadStepHook?: (data: RawData[]) => Promise<RawData[]>;
  selectHeaderStepHook?: (
    headerValues: RawData,
    data: RawData[]
  ) => Promise<{ headerValues: RawData; data: RawData[] }>;
  matchColumnsStepHook?: (table: Data<T>[], rawData: RawData[], columns: any) => Promise<Data<T>[]>;
  onSubmit?: (data: any) => void | Promise<any>;
  conflictFields?: string[];

  constructor(
    name: string,
    applicableSteps: StepType[],
    onNext: (event: StepChangeEvent) => void,
    params: AddonParams<T>
  ) {
    this.name = name;
    this.applicableSteps = applicableSteps;
    this.onNext = onNext;

    this.gridType = params.gridType;
    this.fields = params.fields;
    this.maxRecords = params.maxRecords;
    this.maxFileSize = params.maxFileSize;
    this.autoMapHeaders = params.autoMapHeaders;
    this.autoMapEntityRelationValues = params.autoMapEntityRelationValues;
    this.dateFormat = params.dateFormat;
    this.parseRaw = params.parseRaw;

    this.uploadStepHook = params.uploadStepHook;
    this.selectHeaderStepHook = params.selectHeaderStepHook;
    this.matchColumnsStepHook = params.matchColumnsStepHook;
    this.onSubmit = params.onSubmit;
    this.conflictFields = params.conflictFields || [];

    this.onMount();
  }

  protected abstract getJSXElement(step: StepType, data: any): ReactElement;

  protected onMount(): void {}

  protected onUnmount(): void {}

  protected setUploadedFile(file: File): void {
    this.uploadedFile = file;
  }

  async getExistingData(): Promise<any[]> {
    return [];
  }

  async getExistingRelationData(): Promise<Record<string, any[]>> {
    return {};
  }

  public validate(data: any, step: StepType): ValidationResult {
    return {
      valid: true,
      errors: [],
      warnings: []
    };
  }

  public render(step: StepType, data: any): ReactElement {
    if (!this.applicableSteps.includes(step)) {
      return <></>;
    }
    return this.getJSXElement(step, data);
  }
}
